import { Button, ListItemIcon, ListItemText } from "@mui/material";
import { useCallback, useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedMenuItem from "@/common/components/Auth/AuthorizedMenuItem";
import AppIconButton from "@/common/components/Button/AppIconButton";
import PoolItemCreateManyModal from "@/common/components/Entity/PoolItem/PoolItemCreateManyModal";
import PoolItemPaginatedList, {
  PoolItemPaginatedListPropagatedDepsMap,
} from "@/common/components/Entity/PoolItem/PoolItemPaginatedList";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import { ROUTE_PATH } from "@/common/constants/routing";
import { usePropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { ViewVariant } from "@/common/ts/layout";
import { AppPermission, PoolDto } from "@/core/api/generated/v1";

import PoolItemDeleteAllModal from "../../../PoolItem/PoolItemDeleteAllModal";
import PoolAvailabilityDisplay, {
  PoolAvailabilityDisplayPropagatedDepsMap,
} from "../../PoolAvailabilityDisplay";

interface Props {
  pool: PoolDto;
}

export default function ItemsTabContent({ pool }: Props) {
  const listPropagatedDeps = usePropagatedDeps<PoolItemPaginatedListPropagatedDepsMap>();
  const availabilityPropagatedDeps = usePropagatedDeps<PoolAvailabilityDisplayPropagatedDepsMap>();

  const [isAddPoolItemsModalOpen, setIsAddPoolItemsModelOpen] = useState(false);
  const [isDeleteAllPoolItemsModalOpen, setIsDeleteAllPoolItemsModalOpen] = useState(false);

  const onPooItemsChange = useCallback(() => {
    listPropagatedDeps.trigger("refetch");
    availabilityPropagatedDeps.trigger("refetch");
  }, [listPropagatedDeps]);

  return (
    <PageTabContent>
      <ViewLayout
        sx={{ ".ViewLayoutV2ContentWrapper": { gap: 0, spacing: 0 } }}
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Pool items"}
            primaryAction={
              <Button
                variant='contained'
                color='primary'
                onClick={() => setIsAddPoolItemsModelOpen(true)}
              >
                Add pool items
              </Button>
            }
            secondaryAction={
              <MenuWithTrigger
                withAuthCloseOnClick
                trigger={
                  <AppIconButton sx={{ ml: "auto" }}>
                    <AppIcon of='moreVert' />
                  </AppIconButton>
                }
              >
                <AuthorizedMenuItem
                  permissions={[AppPermission.FleetAppAccess, AppPermission.PoolRead]}
                  component={AppLink}
                  to={ROUTE_PATH.POOL_ITEMS(pool.id)}
                >
                  <ListItemIcon>
                    <AppIcon of='view' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>View all items</ListItemText>
                </AuthorizedMenuItem>
                <AuthorizedMenuItem
                  permissions={[AppPermission.FleetAppAccess, AppPermission.PoolManage]}
                  onClick={() => setIsDeleteAllPoolItemsModalOpen(true)}
                >
                  <ListItemIcon>
                    <AppIcon of='delete' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Delete all items</ListItemText>
                </AuthorizedMenuItem>
              </MenuWithTrigger>
            }
          />
        }
      >
        <PoolAvailabilityDisplay
          sx={{
            ".MuiAlert-root": {
              borderRight: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 0,
            },
          }}
          poolId={pool?.id}
          availability={undefined}
          isLoad
          propagatedDeps={availabilityPropagatedDeps}
        />

        <PoolItemPaginatedList
          poolId={undefined}
          pool={pool}
          displayProps={{
            viewVariant: ViewVariant.Tab,
            breadcrumbs: false,
            header: false,
            filters: false,
          }}
          propagatedDeps={listPropagatedDeps}
          onChange={() => {
            onPooItemsChange();
          }}
        />

        {/* Create many pool items */}
        <PoolItemCreateManyModal
          createManyProps={{
            defaultValues: {
              poolId: pool.id!,
            },
            onSave: () => {
              setIsAddPoolItemsModelOpen(false);
              onPooItemsChange();
            },
          }}
          open={isAddPoolItemsModalOpen}
          onClose={() => {
            setIsAddPoolItemsModelOpen(false);
          }}
        />

        {/* Delete all pool items */}
        <PoolItemDeleteAllModal
          pool={pool}
          open={isDeleteAllPoolItemsModalOpen}
          onClose={() => {
            setIsDeleteAllPoolItemsModalOpen(false);
          }}
          onDeleted={() => {
            setIsDeleteAllPoolItemsModalOpen(false);
            onPooItemsChange();
          }}
        />
      </ViewLayout>
    </PageTabContent>
  );
}
